import React from 'react';
import { diffWords } from 'diff';
import { buildPreviewSegments } from './applyEditsToDoc';

// Renders one edit inline at its landing spot. A replacement (matchLen > 0)
// shows a red-to-green word diff; an insertion shows the green text exactly
// where it lands. Pencil button opens an inline textarea so the clinician
// can refine the suggested text BEFORE accepting it (Cursor pattern: accept,
// reject, or accept-with-edits).
function EditInline({ edit, status, onApprove, onDeny, onEditSave }) {
  const isReplacement = edit.matchLen > 0;
  const [editing, setEditing] = React.useState(false);
  // Pre-populate the textarea with newText. For pure deletions (newText=='')
  // start from oldText so the clinician can KEEP and refine it instead of
  // starting from blank — the common "I want to keep this but tweak it" case.
  const initialBuf = (edit.newText || (isReplacement && !edit.newText ? edit.oldText : '') || '');
  const [buf, setBuf] = React.useState(initialBuf);
  React.useEffect(() => { setBuf(initialBuf); }, [edit.id, edit.newText, edit.oldText, isReplacement]); // eslint-disable-line react-hooks/exhaustive-deps

  const parts = isReplacement && status !== 'denied' && !editing
    ? diffWords(edit.oldText || '', edit.newText || '')
    : null;

  if (editing) {
    return (
      <span className="inline-block align-top w-full my-1">
        <textarea
          value={buf}
          onChange={(e) => setBuf(e.target.value)}
          rows={Math.min(8, Math.max(2, (buf.match(/\n/g) || []).length + 1))}
          className="block w-full text-sm rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 p-2 font-mono"
          autoFocus
        />
        <span className="mx-1 inline-flex items-center gap-1 align-middle">
          <button type="button" aria-label={`save edit ${edit.id}`}
            className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white font-semibold"
            onClick={() => { onEditSave(edit.id, buf); setEditing(false); onApprove(edit.id); }}>
            Save &amp; accept
          </button>
          <button type="button" aria-label={`cancel edit ${edit.id}`}
            className="text-xs px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-700"
            onClick={() => { setBuf(initialBuf); setEditing(false); }}>
            Cancel
          </button>
        </span>
      </span>
    );
  }

  return (
    <span className="inline">
      {isReplacement ? (
        status === 'denied'
          ? <span>{edit.oldText}</span>
          : parts.map((p, i) => (
            <span key={i}
              className={p.added ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
                : p.removed ? 'bg-rose-100 text-rose-800 line-through dark:bg-rose-900/50 dark:text-rose-200' : ''}>
              {p.value}
            </span>
          ))
      ) : (
        status === 'denied'
          ? <span className="line-through text-slate-400">{edit.insert}</span>
          : <span className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">{edit.insert}</span>
      )}
      <span className="mx-1 inline-flex items-center gap-1 align-middle">
        <button type="button" aria-label={`approve ${edit.id}`}
          title="Accept this change"
          className={`text-xs px-1 rounded ${status === 'approved' ? 'bg-emerald-600 text-white' : 'bg-slate-200 dark:bg-slate-700'}`}
          onClick={() => onApprove(edit.id)}>&#10003;</button>
        <button type="button" aria-label={`deny ${edit.id}`}
          title={isReplacement ? 'Reject — keep the original text' : 'Reject — do not insert this'}
          className={`text-xs px-1 rounded ${status === 'denied' ? 'bg-rose-600 text-white' : 'bg-slate-200 dark:bg-slate-700'}`}
          onClick={() => onDeny(edit.id)}>&times;</button>
        <button type="button" aria-label={`edit ${edit.id}`}
          title="Edit the suggestion before accepting"
          className="text-xs px-1 rounded bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
          onClick={() => setEditing(true)}>&#9998;</button>
        {status === 'denied' && isReplacement && (
          <span className="text-[10px] text-rose-500">(keeping original)</span>
        )}
        <span className="text-[10px] text-slate-400">from {edit.source?.label}{edit.rationale ? ` - ${edit.rationale}` : ''}</span>
      </span>
    </span>
  );
}

export default function ProcessDiffReview({
  text,
  locatedEdits,
  approvals,
  onApprove,
  onDeny,
  onEditSave,
  onApproveRest,
  onRejectAllClose,
  onApply,
  applying,
}) {
  const segments = buildPreviewSegments(text, locatedEdits, approvals);
  const totalEdits = (locatedEdits || []).length;
  const approvedCount = Object.values(approvals).filter((s) => s === 'approved').length;
  const deniedCount = Object.values(approvals).filter((s) => s === 'denied').length;
  const pendingCount = Math.max(0, totalEdits - approvedCount - deniedCount);
  const everyDecided = totalEdits > 0 && pendingCount === 0;
  return (
    <div className="grid h-full min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)_auto] gap-3 overflow-hidden">
      <div className="text-[11px] text-slate-500 dark:text-slate-400 px-1 -mb-1">
        Per edit: <span className="font-semibold text-emerald-600">&#10003;</span> keep · <span className="font-semibold text-rose-600">&times;</span> reject (keeps original) · <span className="font-semibold text-amber-600">&#9998;</span> edit before accepting. Then <span className="font-semibold">Apply to note</span> writes only the &#10003; edits.
      </div>
      <div className="whitespace-pre-wrap text-sm leading-relaxed flex-1 min-h-0 overflow-auto rounded border border-slate-200 p-3 dark:border-slate-700">
        {segments.map((s, i) =>
          s.kind === 'context'
            ? <span key={i}>{s.text}</span>
            : <EditInline key={i} edit={s.edit} status={s.status} onApprove={onApprove} onDeny={onDeny} onEditSave={onEditSave} />,
        )}
      </div>
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-white pt-3 dark:border-slate-700 dark:bg-slate-900">
        <button type="button" onClick={onApproveRest} disabled={pendingCount === 0}
          title="Approve every edit you haven't rejected. Your X choices stay rejected."
          className="text-sm font-medium px-3 py-1.5 rounded-lg border border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 disabled:opacity-40 disabled:cursor-default">
          {pendingCount === 0 ? (everyDecided ? 'All decided' : 'Nothing pending') : `Approve rest (${pendingCount})`}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" disabled={applying}
            title="Reject every edit AND mark this source as reviewed (won't show next time)"
            className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/40 disabled:opacity-50"
            onClick={onRejectAllClose}>
            Reject all &amp; mark reviewed
          </button>
          <button type="button" disabled={applying || approvedCount === 0}
            title={approvedCount === 0 ? 'No edits accepted yet — use the green check on at least one edit first' : 'Apply your accepted edits to the note + mark this source as reviewed'}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onApply}>
            {applying ? 'Applying...' : `Apply to note (${approvedCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
